import { Checkbox, FormHelperText, Typography } from '@mui/material';
import { AllocationRule } from 'further-types/lp/allocations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { allocationsHelpers } from 'further-ui/domain';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import api from 'lib/trpcClient';
import round from 'lodash/round';
import sumBy from 'lodash/sumBy';
import React, { useEffect, useRef } from 'react';

export type Allocation = {
  commitmentId: string;
  allocationAmountTcy: number;
  allocationPercentage: number;
  excludeFromAllocation: boolean;
  paidAmountLcy?: number;
};

type Commitment = {
  commitmentId: string;
  investorName: string;
  isCarryPartnerCommitment?: boolean;
  isGeneralPartnerCommitment?: boolean;
};

const getExcludedCommitmentNames = (
  commitments: Array<Commitment>,
  allocations: Array<Allocation>,
  excludeCarryPartnerFromAllocations: boolean,
  excludeGeneralPartnerFromAllocations: boolean,
) => {
  const exclusionSettings = {
    excludeCarryPartnerFromAllocations,
    excludeGeneralPartnerFromAllocations,
  };

  return allocations
    .filter((allocation) => {
      if (!allocation.excludeFromAllocation) return false;
      const commitment = commitments.find(
        (c) => c.commitmentId === allocation.commitmentId,
      );
      if (
        commitment &&
        allocationsHelpers.isSystemExcludedCommitment(
          commitment,
          exclusionSettings,
        )
      )
        return false;
      return true;
    })
    .map(
      (allocation) =>
        commitments.find(
          (commitment) => commitment.commitmentId === allocation.commitmentId,
        )?.investorName ?? '',
    )
    .join(', ');
};

type Props = {
  vehicleId: string;
  allocationRule?: AllocationRule;
  allocationsSourceActionId?: string;
  onAllocationRuleChange: (allocationRule: AllocationRule) => void;
  onAllocationsChange: (allocations: Array<Allocation>) => void;
  allocatedActionLabel: string;
  currency: CurrencyCode;
  baseCurrency: CurrencyCode;
  allocations: Array<Allocation>;
  allocatedAmountTcy: number;
  useGeneralCapital?: boolean;
  error?: string;
  unlinkAction?: () => void;
  currentActionIds?: Array<string>;
  date: CalendarDay;
  reconcilableTransactions?: Array<{
    transactionId: string;
    transactionAmountTcy: number;
  }>;
  disabled?: boolean;
  isOutsideOfCommitment?: boolean;
};

const AllocationsManager: React.FC<Props> = ({
  vehicleId,
  allocationRule = AllocationRule.ByCommitmentAndClosingDate,
  allocations,
  allocationsSourceActionId,
  onAllocationRuleChange,
  onAllocationsChange,
  allocatedActionLabel,
  currency,
  baseCurrency,
  allocatedAmountTcy,
  useGeneralCapital = false,
  error,
  unlinkAction,
  currentActionIds,
  date,
  reconcilableTransactions,
  disabled,
  isOutsideOfCommitment = false,
}) => {
  const useReconciliationMode =
    allocationRule === AllocationRule.Reconciliation;
  const isManualAllocation = allocationRule === AllocationRule.Manual;
  const isLinkedActionAllocation =
    allocationRule === AllocationRule.LinkedAction;
  const showGeneralCapital = useGeneralCapital && currency === baseCurrency;

  const excludedCommitmentIds = allocations
    .filter((allocation) => allocation.excludeFromAllocation)
    .map((allocation) => allocation.commitmentId);

  const { data: commitmentsData } = api.allocations.getCommitments.useQuery({
    vehicleId,
    actionIdsToExclude: currentActionIds ?? [],
    date,
  });
  const commitments = commitmentsData?.commitments ?? [];
  const excludeCarryPartnerFromAllocations =
    commitmentsData?.excludeCarryPartnerFromAllocations ?? false;
  const excludeGeneralPartnerFromAllocations =
    commitmentsData?.excludeGeneralPartnerFromAllocations ?? false;

  const { data, isFetching } = api.allocations.getAllocations.useQuery(
    {
      vehicleId,
      allocationRule,
      allocatedAmountTcy,
      excludedCommitmentIds,
      allocationsSourceActionId,
      reconcilableTransactions,
      currentActionId:
        currentActionIds?.length === 1 ? currentActionIds[0] : undefined,
    },
    {
      enabled: !!allocatedAmountTcy && !isManualAllocation,
    },
  );

  const { data: generalCapitalPerCommitment } =
    api.capital.generalCapitalPerCommitment.useQuery({
      vehicleId,
    });

  const onAllocationsChangeRef = useRef(onAllocationsChange);
  useEffect(() => {
    onAllocationsChangeRef.current = onAllocationsChange;
  }, [onAllocationsChange]);

  useEffect(() => {
    if (!data || isManualAllocation) return;
    onAllocationsChangeRef.current(data);
  }, [data, isManualAllocation]);

  useEffect(() => {
    if (!isManualAllocation || !allocatedAmountTcy) return;

    const recalculated = allocations.map((allocation) => ({
      ...allocation,
      allocationAmountTcy: allocation.excludeFromAllocation
        ? 0
        : round(
            (allocation.allocationPercentage / 100) * allocatedAmountTcy,
            2,
          ),
    }));

    const hasChanged = recalculated.some(
      (a, i) => a.allocationAmountTcy !== allocations[i]?.allocationAmountTcy,
    );

    if (hasChanged) onAllocationsChangeRef.current(recalculated);
  }, [allocatedAmountTcy, isManualAllocation, allocations]);

  const handleAllocationRuleChange = (newAllocationRule: AllocationRule) => {
    onAllocationRuleChange(newAllocationRule);

    if (newAllocationRule !== AllocationRule.LinkedAction) {
      unlinkAction?.();
    }

    if (newAllocationRule === AllocationRule.Manual) {
      const hasExcludedCommitments = allocations.some(
        (allocation) => allocation.excludeFromAllocation,
      );
      if (hasExcludedCommitments) {
        onAllocationsChange(
          allocations.map((allocation) => ({
            ...allocation,
            excludeFromAllocation: false,
          })),
        );
      }
    }
  };

  const handleAllocationAmountChange = (
    value: number,
    commitmentId: string,
  ) => {
    if (!allocatedAmountTcy || isLinkedActionAllocation) return;

    let updatedAllocations = allocations.map((allocation) => {
      if (allocation.commitmentId === commitmentId) {
        return {
          ...allocation,
          allocationAmountTcy: value,
          allocationPercentage: round((value / allocatedAmountTcy) * 100, 5),
        };
      }
      return allocation;
    });

    if (!isManualAllocation) {
      onAllocationRuleChange(AllocationRule.Manual);
      unlinkAction?.();
      if (updatedAllocations.some((a) => a.excludeFromAllocation)) {
        updatedAllocations = updatedAllocations.map((a) => ({
          ...a,
          excludeFromAllocation: false,
        }));
      }
    }

    onAllocationsChange(updatedAllocations);
  };

  const handleAllocationPercentageChange = (
    value: number,
    commitmentId: string,
  ) => {
    if (isLinkedActionAllocation) return;

    handleAllocationAmountChange(
      round((value / 100) * allocatedAmountTcy, 2),
      commitmentId,
    );
  };

  const handleExcludeCommitment = (
    commitmentId: string,
    isIncluded: boolean,
  ) => {
    if (isLinkedActionAllocation) {
      handleAllocationRuleChange(AllocationRule.Manual);
      unlinkAction?.();
    }

    onAllocationsChange(
      allocations.map((allocation) => {
        if (allocation.commitmentId === commitmentId) {
          return {
            ...allocation,
            allocationAmountTcy: isIncluded
              ? allocation.allocationAmountTcy
              : 0,
            allocationPercentage: isIncluded
              ? allocation.allocationPercentage
              : 0,
            excludeFromAllocation: !isIncluded,
          };
        }

        return allocation;
      }),
    );
  };

  const excludedCommitmentNames = getExcludedCommitmentNames(
    commitments,
    allocations,
    excludeCarryPartnerFromAllocations,
    excludeGeneralPartnerFromAllocations,
  );
  const allocationRuleOptions: Array<{
    value: AllocationRule;
    label: string;
    disabled?: boolean;
  }> = useReconciliationMode
    ? [
        {
          value: AllocationRule.Reconciliation,
          label: 'Per selected transactions',
        },
      ]
    : [
        {
          value: AllocationRule.ByCommitmentAndClosingDate,
          label: `By commitment and closing date${
            excludedCommitmentNames
              ? ` excluding ${excludedCommitmentNames}`
              : ''
          }`,
        },
        {
          value: AllocationRule.Manual,
          label: 'Manual allocation',
        },
        ...(allocationsSourceActionId
          ? [
              {
                value: AllocationRule.LinkedAction,
                label: 'Linked action',
                disabled: true,
              },
            ]
          : []),
      ];

  const rows = allocations.map((allocation) => {
    const commitment = commitments.find(
      (commitment) => commitment.commitmentId === allocation.commitmentId,
    );

    return {
      ...allocation,
      id: allocation.commitmentId,
      generalCapitalPreDealLcy:
        generalCapitalPerCommitment?.[allocation.commitmentId] ?? 0,
      investorName: commitment?.investorName ?? '',
      isCarryPartnerCommitment: commitment?.isCarryPartnerCommitment ?? false,
      isGeneralPartnerCommitment:
        commitment?.isGeneralPartnerCommitment ?? false,
      isSystemExcluded:
        allocation.excludeFromAllocation &&
        !!commitment &&
        allocationsHelpers.isSystemExcludedCommitment(commitment, {
          excludeCarryPartnerFromAllocations,
          excludeGeneralPartnerFromAllocations,
        }),
      totalCommitmentLcy: commitment?.totalCommitmentLcy ?? 0,
      unallocatedCommitmentLcy: commitment?.unallocatedCommitmentLcy ?? 0,
      closeDate: commitment?.closeDate,
    };
  });

  const allocationsSum = round(
    sumBy(rows, (allocation) => allocation.allocationAmountTcy),
    2,
  );

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      label: 'Investor',
      key: 'investorLabel',
      render: (row) => (
        <Row centered spacing="sm">
          <Checkbox
            disabled={
              row.isSystemExcluded ||
              isLinkedActionAllocation ||
              useReconciliationMode ||
              isManualAllocation ||
              disabled
            }
            checked={!row.excludeFromAllocation}
            onChange={(e) => {
              handleExcludeCommitment(row.commitmentId, e.target.checked);
            }}
          />
          <Typography variant="h6">{row.investorName}</Typography>
        </Row>
      ),
    },
    ...(!isOutsideOfCommitment
      ? [
          {
            label: 'Total commitment',
            key: 'totalCommitmentLcy',
            render: (row: { totalCommitmentLcy: number }) =>
              numberToCurrencyString(row.totalCommitmentLcy, {
                currency: baseCurrency,
              }),
          },
          {
            label: 'Unallocated commitment',
            key: 'unallocatedCommitmentLcy',
            render: (row: { unallocatedCommitmentLcy: number }) =>
              numberToCurrencyString(row.unallocatedCommitmentLcy, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    ...(showGeneralCapital
      ? [
          {
            label: 'General capital pre-deal',
            key: 'generalCapitalPreDealLcy',
            render: (row: { generalCapitalPreDealLcy: number }) =>
              numberToCurrencyString(row.generalCapitalPreDealLcy, {
                currency: baseCurrency,
              }),
          },
        ]
      : []),
    {
      label: 'Close date',
      key: 'closeDate',
      render: (row) => row.closeDate?.toReadable() ?? '-',
    },
    {
      label: `${allocatedActionLabel} allocated`,
      key: 'allocationAmountTcy',
      render: (row) => (
        <NumberInput
          disabled={
            row.excludeFromAllocation ||
            isLinkedActionAllocation ||
            useReconciliationMode ||
            disabled
          }
          value={row.allocationAmountTcy}
          decimals={2}
          fixedDecimalScale
          currency={currency}
          onChange={(value) => {
            handleAllocationAmountChange(value ?? 0, row.commitmentId);
          }}
        />
      ),
    },
    {
      label: `${allocatedActionLabel} allocated (%)`,
      key: 'allocationPercentage',
      render: (row) => (
        <NumberInput
          disabled={
            row.excludeFromAllocation ||
            isLinkedActionAllocation ||
            useReconciliationMode ||
            disabled
          }
          value={row.allocationPercentage}
          decimals={10}
          showPercentSuffix
          onChange={(value) => {
            handleAllocationPercentageChange(value ?? 0, row.commitmentId);
          }}
        />
      ),
    },
  ];

  if (showGeneralCapital) {
    columns.push({
      label: 'General capital post-deal',
      key: 'generalCapitalPostDealLcy',
      render: (row) =>
        numberToCurrencyString(
          round(row.generalCapitalPreDealLcy - row.allocationAmountTcy, 2),
          {
            currency: baseCurrency,
          },
        ),
    });
  }

  const customFooter = [
    {
      key: 'label',
      content: (
        <Typography variant="body2">
          Total {allocatedActionLabel.toLowerCase()} from {allocations.length}{' '}
          investors
        </Typography>
      ),
      colspan: columns.length - 2,
    },
    {
      key: 'allocatedAmountTcy',
      content: (
        <Typography variant="body2">
          {numberToCurrencyString(allocationsSum, { currency })}
        </Typography>
      ),
    },
    {
      key: 'allocatedPercentage',
      content: (
        <Typography variant="body2">
          {numberToPercentageString(allocationsSum / allocatedAmountTcy)}
        </Typography>
      ),
    },
  ];

  return (
    <Column spacing="lg">
      <FormField label="Allocation rule" size="md">
        <BasicSelect
          fullWidth
          value={allocationRule}
          onChange={handleAllocationRuleChange}
          options={allocationRuleOptions}
          disabled={useReconciliationMode || disabled}
        />
      </FormField>
      {!useReconciliationMode && (
        <Table
          columns={columns}
          rows={rows}
          customFooterRow={customFooter}
          isLoading={isFetching}
          isDisabledRow={(row) => row.isSystemExcluded}
          disabledRowTooltip={(row) =>
            row.isCarryPartnerCommitment ? (
              <span>
                {row.investorName} is set to be excluded from all fund
                allocations (except carried interest, where applicable). To
                change this setting, please go to{' '}
                <i>Fund Setup &gt; Carry settings</i>.
              </span>
            ) : (
              <span>
                {row.investorName} is set to be excluded from all fund
                allocations. To change this setting, please go to{' '}
                <i>Fund Settings &gt; General</i>.
              </span>
            )
          }
        />
      )}
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
    </Column>
  );
};

export default AllocationsManager;
